import React, { createContext } from 'react';
import { UseQueryResult } from 'react-query';

import { IResource } from './ResourceCard';
import { IResourcesFilter } from './ResourcesFilter';
import { IResourceIndexResponse, IResourceUuid } from './interfaces';

export interface ResourcesInfo {
  currentTypeFilters: string[];
  currentCompanyFilters: string[];
  currentPeopleFilters: string[];
  currentClientFilters: string[];
  currentIndustryFilters: string[];
  currentTagCategory: string;
  currentFormatFilters: string[];
  resources: IResource[];
  setResources: React.Dispatch<React.SetStateAction<IResource[]>>;
  resourcesQuery: UseQueryResult<IResourceIndexResponse, Error>;
  clearAllFilters?: () => void;
  setCurrentTypeFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentIndustryFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentFormatFilters: React.Dispatch<React.SetStateAction<string[]>>;
  allRelatedCompanies: IResourcesFilter<any>[];
  allRelatedTypes: IResourcesFilter<any>[];
  allRelatedPeople: IResourcesFilter<any>[];
  allRelatedClients: IResourcesFilter<any>[];
  allRelatedIndustries: IResourcesFilter<any>[];
  allRelatedFormats: IResourcesFilter<any>[];
  setCurrentCompanyFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentPeopleFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentClientFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentTagCategory: React.Dispatch<React.SetStateAction<string>>;
  nextPage: () => void;
  currentPage?: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  allResourcesUuids: IResourceUuid[];
  setAllResourcesUuids: React.Dispatch<React.SetStateAction<IResourceUuid[]>>;
  allOriginalResourcesUuids: IResourceUuid[];
  setAllOriginalResourcesUuids: React.Dispatch<React.SetStateAction<IResourceUuid[]>>;
  allOriginalSamples: IResource[];
  setAllOriginalSamples: React.Dispatch<React.SetStateAction<IResource[]>>;
  showLoadingState: boolean;
  setShowLoadingState: React.Dispatch<React.SetStateAction<boolean>>;
  perfectMatchedFilters: any[];
  setPerfectMatchedFilters: React.Dispatch<React.SetStateAction<any[]>>;
  availableTypesFromFilters: IResourceUuid[];
  availableCompaniesFromFilters: IResourceUuid[];
  availableClientsFromFilters: IResourceUuid[];
  availableIndustriesFromFilters: IResourceUuid[];
  availableFormatsFromFilters: IResourceUuid[];
  nlpTypeFilters: string[];
  nlpCompanyFilters: string[];
  nlpClientFilters: string[];
  nlpIndustryFilters: string[];
  nlpFormatFilters: string[];
  nlpPeopleFilters: string[];
  nlpResourceFilters: string[];
  nlpTagFilters: string[];
  nlpSkillFilters: string[];
  nlpPassionFilters: string[];
  setNlpTypeFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpCompanyFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpClientFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpIndustryFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpFormatFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpPeopleFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpResourceFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpTagFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpSkillFilters: React.Dispatch<React.SetStateAction<string[]>>;
  setNlpPassionFilters: React.Dispatch<React.SetStateAction<string[]>>;
  showEmptyState: boolean;
  setShowEmptyState: React.Dispatch<React.SetStateAction<boolean>>;
  resourcesUuidsFromOriginalRequest: any[];
  resetNlpFilters: () => void;
  resetCurrentFilters: () => void;
}

export const ResourcesContext = createContext<ResourcesInfo>({
  currentTypeFilters: [],
  currentCompanyFilters: [],
  currentPeopleFilters: [],
  currentClientFilters: [],
  currentIndustryFilters: [],
  currentTagCategory: null,
  currentFormatFilters: [],
  resources: [],
  allOriginalSamples: [],
  setAllOriginalSamples: () => {},
  setResources: () => {},
  resourcesQuery: null,
  setCurrentTypeFilters: () => {},
  clearAllFilters: () => {},
  allRelatedCompanies: [],
  allRelatedTypes: [],
  allRelatedPeople: [],
  allRelatedClients: [],
  allRelatedIndustries: [],
  allRelatedFormats: [],
  setCurrentCompanyFilters: () => {},
  setCurrentPeopleFilters: () => {},
  setCurrentClientFilters: () => {},
  setCurrentIndustryFilters: () => {},
  setCurrentFormatFilters: () => {},
  setCurrentTagCategory: () => {},
  nextPage: () => {},
  currentPage: 1,
  allResourcesUuids: [],
  setAllResourcesUuids: () => {},
  setCurrentPage: () => {},
  allOriginalResourcesUuids: [],
  setAllOriginalResourcesUuids: () => {},
  showLoadingState: false,
  setShowLoadingState: () => {},
  perfectMatchedFilters: [],
  setPerfectMatchedFilters: () => {},
  availableTypesFromFilters: [],
  availableCompaniesFromFilters: [],
  availableClientsFromFilters: [],
  availableIndustriesFromFilters: [],
  availableFormatsFromFilters: [],
  nlpTypeFilters: [],
  nlpCompanyFilters: [],
  nlpClientFilters: [],
  nlpIndustryFilters: [],
  nlpFormatFilters: [],
  nlpPeopleFilters: [],
  nlpResourceFilters: [],
  nlpTagFilters: [],
  nlpSkillFilters: [],
  nlpPassionFilters: [],
  setNlpTypeFilters: () => {},
  setNlpCompanyFilters: () => {},
  setNlpClientFilters: () => {},
  setNlpIndustryFilters: () => {},
  setNlpFormatFilters: () => {},
  setNlpPeopleFilters: () => {},
  setNlpResourceFilters: () => {},
  setNlpTagFilters: () => {},
  setNlpSkillFilters: () => {},
  setNlpPassionFilters: () => {},
  showEmptyState: false,
  setShowEmptyState: () => {},
  resourcesUuidsFromOriginalRequest: [],
  resetNlpFilters: () => {},
  resetCurrentFilters: () => {},
});
